import { accountsActions, accountsSelectors, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { Form, Formik } from 'formik';
import { List as ImmutableList } from 'immutable';
// CORE
import React, { useState, useCallback, useMemo, useRef } from 'react';

import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
// MUI
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { businessActions, businessSelectors } from 'app-components/flux/business';
// DATA
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { institutionsSelectors } from 'app-components/flux/institutions';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';

import type {
  AccountSubType,
  AccountType,
  AccountTypeNode,
  BaseAccountProps,
  UsageType,
} from 'app-components/flux/accounts/accountsTypes';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { useFetchOnce } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
// LOCAL
import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import AccountTypeSelector from 'components/Formik/AccountTypeSelector';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import InstitutionIcon from 'components/InstitutionIcon';
import QButton from 'components/QButton';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { noop } from 'lodash';
import type { SetOptional } from 'type-fest';
import UsageAndEntitySelector from '../UsageAndEntitySelector';

export const DIALOG_TYPE_ACCOUNT_EDIT = 'DIALOG_TYPE_ACCOUNT_EDIT';

const useStyles = makeStyles()(({ palette }) => ({
  dialogContent: {
    minWidth: 500,
    display: 'flex',
  },
  tooltip: {
    maxWidth: '150px',
  },
  notice: {
    marginTop: 10,
    display: 'flex',
    padding: '7px 0px 7px 0px',
    borderRadius: 12,
    backgroundColor: palette.action.hover,
  },
  caption: {
    color: palette.text.secondary,
  },
  captionText: {
    margin: 'auto',
  },
  tooltipPlacement: {
    marginBottom: -1,
    marginLeft: 5,
  },
  iconContainer: {
    marginTop: 6,
    width: 64,
  },
  accMargin: {
    marginTop: 16,
  },
  alignment: {
    display: 'flex',
    alignItems: 'center',
  },
}));

type FormSubmitAccountType = { type: AccountType; subType?: AccountSubType };
type StdDialogProps = any; // TODO - switch once this is converted to TS

const dropDown = [
  { label: 'Bank balance (Default)', state: 0, key: 'bank balance' },
  { label: 'Balance with pending', state: 1, key: 'balance with pending' },
];
const menuItems = dropDown.map((item) => (
  <MenuItem key={item.key} value={item.state}>
    {item.label}
  </MenuItem>
));
function AccountEditDialog(props: { accountId: string } & StdDialogProps) {
  const { accountId, ...dialogProps } = props;

  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const businessesById = useAppSelector(businessSelectors.getResourcesById)?.filter((business) => !business.isClosed);
  const defaultBusiness = useAppSelector(businessSelectors.getDefaultBusiness);
  useFetchOnce(businessActions.getBusiness, businessSelectors);

  const stdDialogRef = useRef<StdDialogProps>();

  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, accountId));
  const isManualAccount = accountsUtils.isAccountManual(account);
  const isStripeAccount = accountsUtils.isStripeAccount(account);
  const originalAccountType = account?.type ?? 'UNKNOWN';
  const isARAccount = originalAccountType === 'ACCOUNTS_RECEIVABLE';

  const institutionLogin = useAppSelector((state) =>
    account?.institutionLoginId
      ? institutionLoginsSelectors.getInstitutionLoginForId(state, account.institutionLoginId)
      : undefined,
  );
  const institution = useAppSelector((state) =>
    institutionLogin?.institutionId
      ? institutionsSelectors.getInstitution(state, institutionLogin.institutionId)
      : undefined,
  );

  const actualNormalizeOnlineBalance = account?.aggregators?.first?.()?.normalizeOnlineBalance;

  // ====================================== Component State ================================================ //
  const [updatedAccountName, setUpdatedAccountName] = useState<string | null>(null);
  const [updatedAccountType, setUpdatedAccountType] = useState<FormSubmitAccountType | null>(null);
  const [updatedUsageType, setUpdatedUsageType] = useState<UsageType | null>(null);
  const [normalizeOnlineBalance, setNormalizeOnlineBalance] = useState(actualNormalizeOnlineBalance);

  // ====================================== Redux Selectors ================================================ //
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);

  // ====================================== Callbacks ================================================ //
  const handleUpdateAccount = useCallback(
    (type: AccountType, params = {}) => {
      if (account?.id) {
        const options = { id: account.id, ...params };
        dispatch(
          accountsActions.updateAccount(accountsTypes.mkUpdateAccount(type, options), {
            undo: { userMessage: 'Account updated' },
          }),
        );
      }
    },
    [account, dispatch],
  );

  const handleUpdateAccountNameType = () => {
    stdDialogRef.current?.handleClose?.();
    const params: SetOptional<BaseAccountProps, 'type'> = {};
    if (updatedAccountName) {
      params.name = updatedAccountName;
    }
    if (updatedAccountType) {
      params.subType = updatedAccountType.subType;
    }

    if (updatedUsageType) {
      if (updatedUsageType !== 'PERSONAL') {
        tracker.track('accountConnectedToBusiness');
        params.businessId = updatedUsageType;
        params.usageType = 'BUSINESS';
      } else {
        params.usageType = 'PERSONAL';
      }
    }
    const aggregator = account?.aggregators?.first?.();
    if (aggregator && Boolean(aggregator.normalizeOnlineBalance) !== Boolean(normalizeOnlineBalance)) {
      params.aggregators = ImmutableList([
        aggregator.merge({
          normalizeOnlineBalance,
        }),
      ]);
    }
    handleUpdateAccount(updatedAccountType?.type || originalAccountType, params);
  };

  const updateAccountType = useCallback(({ type, usage }: { type: AccountTypeNode; usage: UsageType }) => {
    setUpdatedAccountType({ type: type.qcsType, subType: type.qcsSubType });
    setUpdatedUsageType(usage);
    // handleUpdateAccount(type.qcsType, { subType: type.qcsSubType });
  }, []);

  const updateBalanceType = useCallback((e: SelectChangeEvent<number>) => {
    setNormalizeOnlineBalance(Boolean(e.target.value));
  }, []);

  const nameAtInstitution = account?.aggregators?.get(0)?.accountName;

  const updatedAccountNameErrorMessage = useMemo(() => {
    if (updatedAccountName === '') return 'Account name cannot be empty';
    if (
      accountsById
        .filter((x) => x.name !== account?.name)
        .some((x) => x?.name?.toLowerCase() === updatedAccountName?.toLowerCase())
    )
      return 'Account name must be unique';
    if (accountsUtils.isInvalidName(updatedAccountName ?? '')) return 'Invalid account name.';
    return null;
  }, [updatedAccountName, accountsById, account]);

  const updatedAccountNameHelperText = useMemo(() => {
    if (updatedAccountNameErrorMessage) return updatedAccountNameErrorMessage;
    return nameAtInstitution ? <span style={{ marginLeft: 15 }}>Name at institution: {nameAtInstitution}</span> : '';
  }, [updatedAccountNameErrorMessage, nameAtInstitution]);

  const initialUsage = (account?.usageType === 'BUSINESS' && (account.businessId || defaultBusiness?.id)) || 'PERSONAL';

  const accountTypeForm = useMemo(() => {
    return (
      <Formik
        initialValues={{
          type: accountsTypes.getAccountTypeNodeByQcsType(originalAccountType, account?.subType),
          usage: initialUsage,
        }}
        validateOnChange
        validateOnBlur={false}
        validate={updateAccountType}
        onSubmit={noop}
      >
        <Form>
          <AccountTypeSelector
            currentAccountType={accountsTypes.getAccountTypeNodeByQcsType(originalAccountType, account?.subType)}
            showManualTypes={isManualAccount}
            sx={{ marginTop: 2, width: '50%' }}
            TextFieldProps={{
              id: 'account_type',
              label: 'Type',
              style: { width: '100%' },
              readOnly: isStripeAccount || isARAccount,
            }}
            name='type'
            disabled={isARAccount}
          />

          {businessEnabled && businessesById?.size > 0 && (
            <Tooltip
              arrow
              placement='right-end'
              title={isStripeAccount ? 'This business must match the one associated with your payment account.' : null}
              classes={{ tooltip: classes.tooltip }}
            >
              <Box sx={{ width: '50%' }}>
                <UsageAndEntitySelector
                  businessesById={businessesById}
                  name={'usage'}
                  id={'usage_type'}
                  label={'Account Purpose'}
                  variant={'filled'}
                  margin={'normal'}
                  size={'normal'}
                  readOnly={isStripeAccount}
                  sx={{
                    flex: 1,
                    '& .MuiSelect-select': {
                      display: 'flex',
                    },
                    flexGrow: 1,
                    width: '100%',
                  }}
                  disabled={isARAccount}
                />
              </Box>
            </Tooltip>
          )}
        </Form>
      </Formik>
    );
  }, [
    originalAccountType,
    account?.subType,
    initialUsage,
    updateAccountType,
    isManualAccount,
    isStripeAccount,
    businessEnabled,
    businessesById,
    classes.tooltip,
    isARAccount,
  ]);

  return (
    <StdDialog showCloseButton stdDialogRef={stdDialogRef} title={'Edit account'} {...dialogProps}>
      <Dump
        obj={{
          props,
          actualNormalizeOnlineBalance,
          normalizeOnlineBalance,
          updatedAccountName,
          updatedAccountType,
          account,
        }}
      />
      <DialogContent className={classes.dialogContent}>
        <div className={classes.iconContainer}>
          <InstitutionIcon institution={institution} />
        </div>
        <div style={{ marginLeft: 10, flexGrow: 1, marginTop: 10 }}>
          <TextField
            label='Name'
            style={{ width: '100%' }}
            variant={'filled'}
            defaultValue={account?.name}
            onChange={(e) => setUpdatedAccountName(e.target.value)}
            error={updatedAccountNameErrorMessage !== null}
            helperText={updatedAccountNameHelperText}
            id={'edit_account_name_field'}
          />

          {accountTypeForm}

          {['BANK', 'CREDIT'].includes(updatedAccountType?.type || originalAccountType) &&
            account?.aggregators?.size &&
            account.aggregators.size > 0 && (
              <Box marginTop={2}>
                <Divider />
                <div className={classes.alignment}>
                  <FormControl className={classes.accMargin} margin='normal' variant='filled'>
                    <InputLabel id='balance-type-label'>Balance type</InputLabel>
                    <Select
                      label='Balance type'
                      labelId='balance-type-select-label'
                      id='balance-type-select'
                      value={normalizeOnlineBalance ? 1 : 0}
                      onChange={updateBalanceType}
                      variant='filled'
                    >
                      {menuItems}
                    </Select>
                  </FormControl>
                  <InfoTooltipIcon
                    className={classes.tooltipPlacement}
                    size='small-no-padding'
                    tooltipText="Set this to 'Bank balance (Default)' if your pending transactions are double counted. This happens when the balance Simplifi is starting with already has pending transactions included. This won't take effect until the next refresh from your bank."
                  />
                </div>
                {!normalizeOnlineBalance ? (
                  <Typography variant='captionRegular' className={classes.caption}>
                    We will show the balance that matches your bank.
                  </Typography>
                ) : (
                  <Typography variant='captionRegular' className={classes.caption}>
                    We will subtract pending transactions when calculating balances.
                  </Typography>
                )}
                {Boolean(account?.aggregators?.first?.()?.normalizeOnlineBalance) !==
                  Boolean(normalizeOnlineBalance) && (
                  <div className={classes.notice}>
                    <Typography variant='captionRegular' className={classes.captionText}>
                      This will take affect after the next successful refresh with your bank.
                    </Typography>
                  </div>
                )}
              </Box>
            )}
        </div>
      </DialogContent>
      <DialogActions>
        <QButton
          id={'edit_account_cancel_button'}
          variant={'outlined'}
          onClick={() => stdDialogRef.current?.handleClose?.()}
        >
          Cancel
        </QButton>

        <QButton
          variant={'contained'}
          disabled={
            ((!updatedAccountName || updatedAccountName === account?.name) &&
              !updatedAccountType &&
              Boolean(actualNormalizeOnlineBalance) === Boolean(normalizeOnlineBalance)) ||
            updatedAccountNameErrorMessage !== null
          }
          onClick={handleUpdateAccountNameType}
          id={'edit_account_update_button'}
        >
          Update
        </QButton>
      </DialogActions>
    </StdDialog>
  );
}

export default AccountEditDialog;
